import { CreativeType } from 'core/creative/Creative';
import _ from 'lodash';
import i18n from 'i18n';
import { CreativeFormBasicData } from '../FormContent/FormContentModel';
import { ImageMediaSummary } from './ImageMediaSummary';
import { CloudStorageManager, GCSManager } from 'core/cloudStorage/CloudStorageManager';
import React from 'react';
import { ImageMultipleMediaSummary } from './ImageMultipleMediaSummary';
import { ProductNativeMediaSummary } from './ProductNativeMediaSummary';
import { RichProductNativeMediaSummary } from './RichProductNativeMediaSummary';
import { RichMediaMediaSummary } from './RichMediaMediaSummary';
import { CreativeManager, DefaultCreativeManager } from 'core/creative/CreativeManager';
import {
  RICH_MEDIA_TEMPLATE_ID,
  RICH_MEDIA_TYPE_I18N_KEY,
  RichMediaType,
  RichMediaDeliveryPosition,
  MARQUEE_IMAGE_COUNT,
  getMarqueeImageMediaKey,
  getMarqueeImageSize,
  isMarqueeRichMediaType
} from '../FormContent/RichMediaFormModel';

export interface CreativeSummaryModel {
  getCreativeBasicSummaryData: (
    getUploadedFileData?: (file: File) => Promise<any>,
    addUploadedFilesData?: (file: File, data: any) => Promise<void>
  ) => Promise<any>;
  getMediaSummaryComponent: () => any;
  getMediaSummary: () => any;
  getBasicSubmitData: () => Promise<any>;
  getJsonSubmitData: () => Promise<any>;
}

abstract class DefaultCreativeSummaryModel implements CreativeSummaryModel {

  constructor (
    protected creativeBasicFormData: CreativeFormBasicData,
    protected cloudStorageManager: CloudStorageManager = new GCSManager()
  ) {}

  getI18nOfValueByKey (key, value) {
    switch (key) {
      case 'creativeType':
        return i18n.t<string>(`creativeType.${_.camelCase(CreativeType[value])}`);
      default:
        return value;
    }
  }

  transferObjectToSummaryData (data, keyPrefix = '') {
    if (!data) {
      return [];
    }
    return _.flattenDeep(Object.keys(data).map(key => {
      const currentKey = _.camelCase(`${keyPrefix}-${key}`);
      if (typeof data[key] === 'object' && !React.isValidElement(data[key])) {
        return this.transferObjectToSummaryData(data[key], currentKey);
      }
      const i18nKey = `creativeSetupFlow.labels.${currentKey}`;
      if (!i18n.exists(i18nKey)) {
        return null;
      }
      return {
        label: i18n.t<string>(i18nKey),
        value: this.getI18nOfValueByKey(currentKey, data[key])
      };
    }));
  }

  async getCreativeBasicSummaryData () {
    const omitProps = ['advertiserId', 'medias', 'typeProperties', 'bannerExtra', 'bannerImageUrl', 'tenmaxCategory', 'retail'];
    const basic = _.omitBy(_.omit(this.creativeBasicFormData, omitProps), _.isUndefined);
    return _.compact([
      ...this.transferObjectToSummaryData(basic),
      ...this.transferObjectToSummaryData(this.getTypePropertiesSummary())
    ]);
  }

  setBasicFormData (formData, omitKeys: string[]) {
    const basic = _.omit(this.creativeBasicFormData, omitKeys);
    for (let key in basic) {
      if (basic[key] !== undefined && basic[key] !== null) {
        formData.append(`creative.${key}`, basic[key]);
      }
    }
  }

  setBasicJson (json, omitKeys: string[]) {
    const basic = _.omit(this.creativeBasicFormData, omitKeys);
    for (let key in basic) {
      if (basic[key] !== undefined && basic[key] !== null) {
        json[key] = basic[key];
      }
    }
  }

  abstract getMediaSummaryComponent ();
  abstract getBasicSubmitData ();
  abstract getJsonSubmitData ();

  getTypePropertiesSummary (): any {
    return {};
  }

  getMediaSummary (): any {
    const medias = this.creativeBasicFormData.medias;
    if (!medias) {
      return {};
    }
    return {
      medias: this.creativeBasicFormData.medias
    };
  }
}

export class ImageSummaryModel extends DefaultCreativeSummaryModel {

  getMediaSummaryComponent (): any {
    return ImageMediaSummary;
  }

  async getBasicSubmitData (): Promise<any> {
    const formData = new FormData();
    this.setBasicFormData(formData, ['advertiserId', 'creativeId', 'typeProperties', 'medias']);
    this.creativeBasicFormData['creativeId'] && formData.append('creative.id', this.creativeBasicFormData['creativeId'].toString());
    formData.append('creative.advertiserId', this.creativeBasicFormData['advertiserId'] ? this.creativeBasicFormData['advertiserId'].toString() : '0');
    const medias = _.get(this.creativeBasicFormData, 'medias', {});
    const imageData = _.get(medias, 'image', {});
    formData.append('creative.typeProperties[width]', imageData.width);
    formData.append('creative.typeProperties[height]', imageData.height);
    let imageUrl = imageData.url;
    if (imageData.file) {
      imageUrl = await this.cloudStorageManager.uploadFileToCloud(imageData.file);
    }
    if (imageUrl) {
      formData.append('creative.typeProperties[imageUrl]', imageUrl);
    }

    return formData;
  }

  async getJsonSubmitData (): Promise<any> {
    let jsonData: any = {
      typeProperties: {}
    };
    this.setBasicJson(jsonData, ['advertiserId', 'creativeId', 'typeProperties', 'medias']);
    if (this.creativeBasicFormData['creativeId']) {
      jsonData.id = this.creativeBasicFormData['creativeId'];
    }
    jsonData.advertiserId = this.creativeBasicFormData['advertiserId'] ? this.creativeBasicFormData['advertiserId'] : 0;
    const medias = _.get(this.creativeBasicFormData, 'medias', {});
    const imageData = _.get(medias, 'image', {});
    jsonData.typeProperties.width = imageData.width.toString();
    jsonData.typeProperties.height = imageData.height.toString();
    let imageUrl = imageData.url;
    if (imageData.file) {
      imageUrl = await this.cloudStorageManager.uploadFileToCloud(imageData.file);
    }
    if (imageUrl) {
      jsonData.typeProperties.imageUrl = imageUrl;
    }

    return jsonData;
  }
}

export class ProductNativeSummaryModel extends DefaultCreativeSummaryModel {

  getMediaSummaryComponent (): any {
    return ProductNativeMediaSummary;
  }

  async getCreativeBasicSummaryData () {
    const basic = _.omit(this.creativeBasicFormData, ['advertiserId', 'medias', 'typeProperties', 'bannerExtra', 'bannerImageUrl', 'products', 'product', 'tenmaxCategory', 'retail', 'relatedProductInfo']);
    return _.compact([
      ...this.transferObjectToSummaryData(basic),
      ...this.transferObjectToSummaryData(this.getTypePropertiesSummary())
    ]);
  }

  getTypePropertiesSummary () {
    const typeProperties = this.creativeBasicFormData.typeProperties;
    if (!typeProperties) {
      return {};
    }
    const product = _.get(typeProperties, 'product');
    return _.omitBy({
      product: product ? getProductDescription(product) : ''
    }, _.isEmpty);
  }

  async getBasicSubmitData (): Promise<any> {
    const formData = new FormData();
    this.setBasicFormData(formData, ['advertiserId', 'creativeId', 'typeProperties', 'medias', 'products', 'product']);
    this.creativeBasicFormData['creativeId'] && formData.append('creative.id', this.creativeBasicFormData['creativeId'].toString());
    formData.append('creative.advertiserId', this.creativeBasicFormData['advertiserId'] ? this.creativeBasicFormData['advertiserId'].toString() : '0');
    const product = _.get(this.creativeBasicFormData, 'typeProperties.product', {});
    if (!_.isEmpty(product)) {
      formData.append('creative.typeProperties[product]', JSON.stringify(product));
    }

    return formData;
  }

  async getJsonSubmitData (): Promise<any> {
    let jsonData: any = {
      typeProperties: {}
    };
    this.setBasicJson(jsonData, ['advertiserId', 'creativeId', 'typeProperties', 'medias', 'products', 'product']);
    if (this.creativeBasicFormData['creativeId']) {
      jsonData.id = this.creativeBasicFormData['creativeId'];
    }
    jsonData.advertiserId = this.creativeBasicFormData['advertiserId'] ? this.creativeBasicFormData['advertiserId'] : 0;
    const product = _.get(this.creativeBasicFormData, 'typeProperties.product', {});
    if (!_.isEmpty(product)) {
      jsonData.typeProperties.product = JSON.stringify(product);
    }

    return jsonData;
  }
}

export class CreateProductNativeSummaryModel extends ProductNativeSummaryModel {

  getTypePropertiesSummary () {
    const typeProperties = this.creativeBasicFormData.typeProperties;
    if (!typeProperties) {
      return {};
    }
    return _.omitBy({
      products: _.get(typeProperties, 'products', []).map((product, index) => {
        return `${index + 1} ${getProductDescription(product)}`;
      }).join('\r\n')
    }, _.isEmpty);
  }

  async getBasicSubmitData (): Promise<any> {
    const submitData: any[] = [];
    const products = _.get(this.creativeBasicFormData, 'typeProperties.products', []);
    _.forEach(products, (product) => {
      const creative = {
        creative: {
          creativeType: this.creativeBasicFormData.creativeType,
          advertiserId: this.creativeBasicFormData.advertiserId,
          tenmaxCategory: this.creativeBasicFormData.tenmaxCategory,
          typeProperties: {
            product: JSON.stringify({
              productId: product.productId,
              name: product.name,
              imgLink: product.imgLink,
              multipack: product.multipack
            })
          }
        }
      };
      submitData.push(creative);
    });
    return submitData;
  }

  async getJsonSubmitData (): Promise<any> {
    return this.getBasicSubmitData();
  }
}

export class MultiImageSummaryModel extends ImageSummaryModel {

  getMediaSummaryComponent (): any {
    return ImageMultipleMediaSummary;
  }

  async getBasicSubmitData () {
    const medias = _.get(this.creativeBasicFormData, 'medias', {});
    const imagesData = _.get(medias, 'images', {});
    const imagesArray: { file: File, width: number, height: number } [] = Object.values(imagesData);
    const submitData: any[] = [];
    const sizeImageMap: { [key: string]: { file: File, width: number, height: number }[] } = imagesArray.reduce((acc, image) => {
      const sizeDes = `${image.width}x${image.height}`;
      if (sizeDes in acc) {
        acc[sizeDes].push(image);
      } else {
        acc[sizeDes] = [image];
      }
      return acc;
    }, {});
    for (let size in sizeImageMap) {
      const imagesArray = sizeImageMap[size];
      for (let i = 0; i < imagesArray.length; ++i) {
        const image = imagesArray[i];
        const imageUrl = await this.cloudStorageManager.uploadFileToCloud(image.file);
        let name = `${this.creativeBasicFormData.name}_${size}`;
        if (imagesArray.length > 1) {
          name = `${name}(${i + 1})`;
        }
        submitData.push({
          creative: {
            name,
            landingPageUrl: this.creativeBasicFormData.landingPageUrl,
            creativeType: this.creativeBasicFormData.creativeType,
            advertiserId: this.creativeBasicFormData.advertiserId,
            tenmaxCategory: this.creativeBasicFormData.tenmaxCategory,
            bannerUrl: this.creativeBasicFormData.bannerUrl,
            typeProperties: {
              width: image.width.toString(),
              height: image.height.toString(),
              imageUrl
            }
          }
        });
      }
    }

    return submitData;
  }

  async getJsonSubmitData (): Promise<any> {
    return this.getBasicSubmitData();
  }
}

export class RichProductNativeSummaryModel extends DefaultCreativeSummaryModel {

  imageUrl?: string;
  adServingUrl?: string;

  constructor (
    creativeBasicFormData: CreativeFormBasicData,
    cloudStorageManager: CloudStorageManager = new GCSManager()
  ) {
    super(creativeBasicFormData, cloudStorageManager);
  }

  getMediaSummaryComponent (): any {
    return RichProductNativeMediaSummary;
  }

  async getCreativeBasicSummaryData () {
    const basic = _.omit(this.creativeBasicFormData, ['advertiserId', 'medias', 'typeProperties', 'bannerExtra', 'bannerImageUrl', 'products', 'product', 'tenmaxCategory', 'retail', 'relatedProductInfo']);
    const medias = _.get(this.creativeBasicFormData, 'medias', {});
    const imageData = _.get(medias, 'image', {});
    this.imageUrl = imageData.url;
    if (imageData.file) {
      this.imageUrl = await this.cloudStorageManager.uploadFileToCloud(imageData.file);
    }

    return _.compact([
      ...this.transferObjectToSummaryData(basic),
      ...this.transferObjectToSummaryData(this.getTypePropertiesSummary())
    ]);
  }

  getTypePropertiesSummary () {
    const typeProperties = this.creativeBasicFormData.typeProperties;
    if (!typeProperties) {
      return {};
    }
    const basicSummary = super.getTypePropertiesSummary();
    return _.omitBy({
      ...basicSummary,
      products: _.get(typeProperties, 'products', []).map((product, index) => {
        return `${index + 1} ${getProductDescription(product)}`;
      }).join('\r\n')
    }, _.isEmpty);
  }

  getMediaSummary (): any {
    const image = _.get(this.creativeBasicFormData, 'medias.image', {});
    if (!this.imageUrl || !image) {
      return {};
    }
    return {
      medias: {
        image: {
          ...image,
          url: this.imageUrl
        }
      }
    };
  }

  getCreativeNameToSubmit (_1: string) {
    return this.creativeBasicFormData.name;
  }

  async getBasicSubmitData (): Promise<any> {
    const submitData: any[] = [];
    const allRelatedProducts = this.creativeBasicFormData.relatedProductInfo || [];
    const productIdCreativeIdMap = allRelatedProducts.reduce((map, product) => {
      map[product.id] = product.creativeId;
      return map;
    }, {} as { [key: string]: number });
    const products = _.get(this.creativeBasicFormData, 'typeProperties.products', []);
    const image = _.get(this.creativeBasicFormData, 'medias.image', {});
    const imageData = {
      width: image.width.toString(),
      height: image.height.toString(),
      imageUrl: this.imageUrl
    };
    if (image.file && !this.imageUrl) {
      imageData.imageUrl = await this.cloudStorageManager.uploadFileToCloud(image.file);
    }

    _.forEach(products, (product) => {
      const creative = {
        creative: _.omitBy({
          creativeId: productIdCreativeIdMap[product.productId],
          name: this.getCreativeNameToSubmit(product.name),
          creativeType: this.creativeBasicFormData.creativeType,
          advertiserId: this.creativeBasicFormData.advertiserId,
          tenmaxCategory: this.creativeBasicFormData.tenmaxCategory,
          typeProperties: {
            ...imageData,
            product: JSON.stringify({
              productId: product.productId,
              name: product.name,
              imgLink: product.imgLink,
              multipack: product.multipack
            })
          }
        }, _.isUndefined)
      };
      submitData.push(creative);
    });
    return submitData;
  }

  async getJsonSubmitData (): Promise<any> {
    return this.getBasicSubmitData();
  }
}

export class CreateRichProductNativeSummaryModel extends RichProductNativeSummaryModel {

  override getCreativeNameToSubmit (productName: string) {
    return `${this.creativeBasicFormData.name} - ${productName}`;
  }
}

export class WebRichProductNativeSummaryModel extends RichProductNativeSummaryModel {
}

export class BrandLedSummaryModel extends RichProductNativeSummaryModel {
}

export class CreateWebRichProductNativeSummaryModel extends CreateRichProductNativeSummaryModel {
}

export class CreateBrandLedSummaryModel extends CreateRichProductNativeSummaryModel {
}

export class RichMediaSummaryModel extends DefaultCreativeSummaryModel {

  constructor (
    creativeBasicFormData: CreativeFormBasicData,
    cloudStorageManager: CloudStorageManager = new GCSManager(),
    protected creativeManager: CreativeManager = new DefaultCreativeManager()
  ) {
    super(creativeBasicFormData, cloudStorageManager);
  }

  getMediaSummaryComponent (): any {
    return RichMediaMediaSummary;
  }

  getMediaSummary (): any {
    return {
      ...super.getMediaSummary(),
      richMediaType: _.get(this.creativeBasicFormData.typeProperties, 'richMediaType', RichMediaType.IMAGE_IMAGE),
      bannerUrl: this.creativeBasicFormData.bannerUrl
    };
  }

  async getCreativeBasicSummaryData () {
    // bannerUrl 改由 getTypePropertiesSummary 以 richMediaLandingPageUrl（導外連結）呈現，
    // 故從 basic 移除，避免同一個值出現兩次（Review 頁需求 C1）。
    const basic = _.omit(this.creativeBasicFormData, ['advertiserId', 'medias', 'typeProperties', 'bannerExtra', 'bannerImageUrl', 'bannerUrl', 'products', 'product', 'tenmaxCategory', 'retail', 'relatedProductInfo']);
    return _.compact([
      ...this.transferObjectToSummaryData(basic),
      ...this.transferObjectToSummaryData(this.getTypePropertiesSummary())
    ]);
  }

  getTypePropertiesSummary () {
    const typeProperties = this.creativeBasicFormData.typeProperties;
    if (!typeProperties) {
      return {};
    }
    const richMediaType = _.get(typeProperties, 'richMediaType', RichMediaType.IMAGE_IMAGE);
    const deliveryPosition = _.get(typeProperties, 'deliveryPosition', RichMediaDeliveryPosition.IN_SITE);
    const products = _.get(typeProperties, 'products', []);
    return _.omitBy({
      richMediaType: i18n.t<string>(
        RICH_MEDIA_TYPE_I18N_KEY[richMediaType] ?? RICH_MEDIA_TYPE_I18N_KEY[RichMediaType.IMAGE_IMAGE]
      ),
      deliveryPosition: i18n.t<string>(
        deliveryPosition === RichMediaDeliveryPosition.OFF_SITE
          ? 'creativeSetupFlow.labels.richMediaOffSite'
          : 'creativeSetupFlow.labels.richMediaInSite'
      ),
      product: products.length > 0 ? getProductDescription(products[0]) : '',
      richMediaLandingPageUrl: _.defaultTo(this.creativeBasicFormData.bannerUrl, '')
    }, _.isEmpty);
  }

  getCreativeNameToSubmit () {
    return this.creativeBasicFormData.name;
  }

  // 組出單一 creative 的資料（含雲端上傳）；barImage / expand 為巢狀物件。
  // 後端 typeProperties 只收 string，數字（width/height/duration）先轉成字串；undefined 維持原狀讓 omitBy 移除。
  private async buildCreativeData (): Promise<any> {
    const toStr = (value: number | undefined): string | undefined =>
      value !== undefined ? value.toString() : undefined;
    const typeProperties = this.creativeBasicFormData.typeProperties || {};
    const medias = _.get(this.creativeBasicFormData, 'medias', {});
    const richMediaType = _.get(typeProperties, 'richMediaType', RichMediaType.IMAGE_IMAGE);
    const deliveryPosition = _.get(typeProperties, 'deliveryPosition', RichMediaDeliveryPosition.IN_SITE);
    const templateId = RICH_MEDIA_TEMPLATE_ID[richMediaType];
    const isVideo = richMediaType === RichMediaType.IMAGE_VIDEO;
    const isMarquee = isMarqueeRichMediaType(richMediaType);

    // 跑馬燈：三張圖依序上傳，陣列順序即輪播順序；不產出 barImage / expand。
    // 三個欄位都是必填，且不 skip 空欄位，讓陣列索引恆等於輪播順序（skip 會讓第 3 張遞補成第 2 張）。
    // width / height 一律送字串（後端 ImageCreativeValue(Map) 以 (String) cast 後 parseInt），
    // 且編輯時未重傳的圖也要帶齊 width / height / imageUrl 三個 key —— 回填資料若缺尺寸，
    // 以版型的固定尺寸補上，避免 JSON.stringify 把 undefined 的 key 整個丟掉。
    let marqueeImages: any[] | undefined;
    if (isMarquee) {
      const marqueeImageSize = getMarqueeImageSize(richMediaType);
      marqueeImages = [];
      for (let index = 0; index < MARQUEE_IMAGE_COUNT; index++) {
        const marqueeImage = _.get(medias, getMarqueeImageMediaKey(index), {});
        let marqueeImageUrl = marqueeImage.url;
        if (marqueeImage.file) {
          marqueeImageUrl = await this.cloudStorageManager.uploadFileToCloud(marqueeImage.file);
        }
        marqueeImages.push({
          width: toStr(_.defaultTo(marqueeImage.width, marqueeImageSize.width)),
          height: toStr(_.defaultTo(marqueeImage.height, marqueeImageSize.height)),
          imageUrl: marqueeImageUrl
        });
      }
    }

    // 置底圖
    const barImage = isMarquee ? {} as any : _.get(medias, 'barImage', {});
    let barImageUrl = barImage.url;
    if (barImage.file) {
      barImageUrl = await this.cloudStorageManager.uploadFileToCloud(barImage.file);
    }

    // 主素材（圖 或 影片）
    let expand: any;
    if (isMarquee) {
      expand = undefined;
    } else if (isVideo) {
      const video = _.get(medias, 'expandVideo', {});
      expand = _.omitBy({
        width: toStr(video.width),
        height: toStr(video.height),
        duration: video.duration !== undefined ? Math.floor(video.duration).toString() : undefined,
        videoUrl: video.url,
        videoAssetId: video.videoAssetId
      }, _.isUndefined);
      if (video.file) {
        expand.videoUrl = await this.cloudStorageManager.uploadFileToCloud(video.file, true);
      }
    } else {
      const expandImage = _.get(medias, 'expandImage', {});
      let expandImageUrl = expandImage.url;
      if (expandImage.file) {
        expandImageUrl = await this.cloudStorageManager.uploadFileToCloud(expandImage.file);
      }
      expand = _.omitBy({
        width: toStr(expandImage.width),
        height: toStr(expandImage.height),
        imageUrl: expandImageUrl
      }, _.isUndefined);
    }

    const products = _.get(typeProperties, 'products', []);
    const product = products.length > 0
      ? JSON.stringify({
        productId: products[0].productId,
        name: products[0].name,
        imgLink: products[0].imgLink,
        multipack: products[0].multipack
      })
      : undefined;

    const creativeTypeProperties = _.omitBy({
      templateId,
      deliveryPosition,
      product,
      barImage: isMarquee
        ? undefined
        : _.omitBy({
          width: toStr(barImage.width),
          height: toStr(barImage.height),
          imageUrl: barImageUrl
        }, _.isUndefined),
      expand,
      marqueeImages
    }, _.isUndefined);

    return _.omitBy({
      creativeId: this.creativeBasicFormData.creativeId,
      name: this.getCreativeNameToSubmit(),
      bannerUrl: this.creativeBasicFormData.bannerUrl,
      landingPageUrl: this.creativeBasicFormData.landingPageUrl,
      creativeType: this.creativeBasicFormData.creativeType,
      advertiserId: this.creativeBasicFormData.advertiserId,
      tenmaxCategory: this.creativeBasicFormData.tenmaxCategory,
      typeProperties: creativeTypeProperties
    }, _.isUndefined);
  }

  // Rich Media 走一般單筆 create / update（POST v2/creatives/、v2/creatives/{id}），
  // 以 multipart FormData 送出；巢狀物件（barImage / expand）以 JSON 字串塞進單一 key。
  async getBasicSubmitData (): Promise<any> {
    const creative = await this.buildCreativeData();
    const formData = new FormData();
    const append = (key: string, value: any) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value.toString());
      }
    };

    creative.creativeId !== undefined && append('creative.id', creative.creativeId);
    append('creative.advertiserId', creative.advertiserId !== undefined ? creative.advertiserId : 0);
    append('creative.name', creative.name);
    append('creative.bannerUrl', creative.bannerUrl);
    append('creative.landingPageUrl', creative.landingPageUrl);
    append('creative.creativeType', creative.creativeType);
    append('creative.tenmaxCategory', creative.tenmaxCategory);

    // typeProperties 是 Map<String, Object>，各 value 非巢狀 Map，無法用 typeProperties[barImage][height] 綁定；
    // 巢狀物件（barImage / expand）與 product 一樣以 JSON 字串塞進單一 key。
    const tp = creative.typeProperties || {};
    append('creative.typeProperties[templateId]', tp.templateId);
    append('creative.typeProperties[deliveryPosition]', tp.deliveryPosition);
    append('creative.typeProperties[product]', tp.product);
    if (!_.isEmpty(tp.barImage)) {
      append('creative.typeProperties[barImage]', JSON.stringify(tp.barImage));
    }
    if (!_.isEmpty(tp.expand)) {
      append('creative.typeProperties[expand]', JSON.stringify(tp.expand));
    }
    // 跑馬燈：陣列同樣以 JSON 字串塞進單一 key（multipart 路徑無法綁定巢狀結構）
    if (!_.isEmpty(tp.marqueeImages)) {
      append('creative.typeProperties[marqueeImages]', JSON.stringify(tp.marqueeImages));
    }

    return formData;
  }

  async getJsonSubmitData (): Promise<any> {
    return [{ creative: await this.buildCreativeData() }];
  }
}

function getProductDescription (product) {
  return `${product.name} (ID: ${product.productId})`;
}
